<template>
    <div class="modal fade" id="privilegeCheckModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ i18n('All privilege checks') }}</h5>
                    <button type="button" class="close" aria-label="Close" @click="close"><span>&times;</span></button>
                </div>
                <div class="modal-body">
                    <div class="form-row align-items-end">
                        <div class="form-group col-md-4 mb-2">
                            <label>PINFL</label>
                            <input v-model="pinfl" class="form-control base_input" maxlength="14" :disabled="loading"
                                   @input="pinfl = pinfl.replace(/\D/g, '').slice(0, 14)" @keyup.enter="check">
                        </div>
                        <div class="form-group col-md-4 mb-2">
                            <label>{{ i18n('Passport series') }}</label>
                            <input v-model="passportNumber" class="form-control base_input text-uppercase" maxlength="12"
                                   :disabled="loading" @input="passportNumber = normalizePassport(passportNumber)" @keyup.enter="check">
                        </div>
                        <div class="form-group col-md-4 mb-2">
                            <button type="button" class="btn base_btn btn-block" :disabled="loading" @click="check">
                                <i v-if="loading" class="mdi mdi-loading mdi-spin mr-1"></i>{{ loading ? i18n('Checking') + '...' : i18n('Check') }}
                            </button>
                        </div>
                    </div>
                    <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>
                    <template v-if="hasChecked">
                        <div v-for="result in results" :key="result.id" class="card mb-2 privilege-result-card"
                             :class="{ 'privilege-result-found': result.status === 'found' }">
                            <button type="button" class="card-header privilege-result-toggle" :aria-expanded="expandedResultId === result.id ? 'true' : 'false'"
                                    :aria-controls="'privilege-result-' + result.id" @click="toggleResult(result.id)">
                                <strong class="small">{{ resultTitle(result) }}</strong>
                                <span class="privilege-result-heading-right">
                                    <span v-if="result.status === 'loading'" class="text-muted small"><i class="mdi mdi-loading mdi-spin mr-1"></i>{{ i18n('Checking') }}...</span>
                                    <span v-else class="badge" :class="badgeClass(result.status)">{{ statusLabel(result.status) }}</span>
                                    <i class="mdi" :class="expandedResultId === result.id ? 'mdi-chevron-up' : 'mdi-chevron-down'" aria-hidden="true"></i>
                                </span>
                            </button>
                            <div v-show="expandedResultId === result.id" :id="'privilege-result-' + result.id" class="card-body py-3">
                                <div v-if="result.status === 'loading'" class="text-muted small">{{ i18n('Checking') }}...</div>
                                <div v-else-if="result.status === 'error'" class="text-muted small">{{ i18n('Privilege service unavailable') }}</div>
                                <div v-else-if="result.status === 'not_found'" class="text-muted small">{{ i18n('No privilege information found') }}</div>
                                <template v-else>
                                    <div v-if="result.status === 'partial'" class="alert alert-warning small py-2 mb-2">{{ i18n('Privilege check incomplete') }}</div>
                                    <div v-if="result.key === 'disabled_youth_vocational_training'" class="text-muted small mb-2">{{ i18n('Vocational training status unconfirmed') }}</div>
                                    <div v-if="isPrivilegeUsed(result)" class="alert alert-warning small py-2 mb-2">{{ i18n('Privilege already used') }}</div>
                                    <div v-for="(group, groupIndex) in resultGroups(result)" :key="groupIndex" class="privilege-detail-group">
                                        <strong v-if="group.title" class="small d-block mb-2">{{ i18n(group.title) }} {{ group.index }}</strong>
                                        <dl class="row mb-0 small">
                                            <template v-for="field in group.fields">
                                                <dt :key="field.label + ':label'" class="col-sm-5 mb-1 text-muted">{{ i18n(field.label) }}</dt>
                                                <dd :key="field.label + ':value'" class="col-sm-7 mb-1">{{ i18n(field.value) }}</dd>
                                            </template>
                                        </dl>
                                    </div>
                                    <div v-if="!resultGroups(result).length" class="text-muted small">{{ i18n('No additional details available') }}</div>
                                </template>
                            </div>
                        </div>

                        <div class="card mt-3">
                            <button type="button" class="card-header privilege-result-toggle" :aria-expanded="expandedResultId === 'last-ticket' ? 'true' : 'false'"
                                    aria-controls="privilege-last-ticket" @click="toggleResult('last-ticket')">
                                <strong class="small">{{ i18n('Last privilege ticket') }}</strong>
                                <i class="mdi" :class="expandedResultId === 'last-ticket' ? 'mdi-chevron-up' : 'mdi-chevron-down'" aria-hidden="true"></i>
                            </button>
                            <div v-show="expandedResultId === 'last-ticket'" id="privilege-last-ticket" class="card-body py-3">
                                <div v-if="lastTicketLoading" class="text-muted small mt-2"><i class="mdi mdi-loading mdi-spin mr-1"></i>{{ i18n('Checking') }}...</div>
                                <dl v-else-if="lastPrivilegeTicket" class="row mb-0 mt-2 small">
                                    <dt class="col-sm-5 mb-1">{{ i18n('Ticket number') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ lastPrivilegeTicket.ticketId }}</dd>
                                    <dt class="col-sm-5 mb-1">{{ i18n('Ticket date') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ lastPrivilegeTicket.ticketDate }}</dd>
                                    <dt class="col-sm-5 mb-1">{{ i18n('Route') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ lastPrivilegeTicket.fromRegion }} — {{ lastPrivilegeTicket.toRegion }}</dd>
                                    <dt class="col-sm-5 mb-1">{{ i18n('Ticket type') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ lastPrivilegeTicket.ticketType }}</dd>
                                    <dt class="col-sm-5 mb-1">{{ i18n('Ticket cost') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ formatMoney(lastPrivilegeTicket.ticketCost) }}</dd>
                                    <dt class="col-sm-5 mb-1">{{ i18n('Paid amount') }}</dt>
                                    <dd class="col-sm-7 mb-1">{{ formatMoney(lastPrivilegeTicket.paidAmount) }}</dd>
                                </dl>
                                <div v-else-if="lastTicketError" class="text-muted small mt-2">{{ i18n('No privilege ticket history found') }}</div>
                                <div v-else class="text-muted small mt-2">{{ i18n('No privilege ticket history found') }}</div>
                            </div>
                        </div>
                    </template>
                </div>
                <div class="modal-footer"><button type="button" class="btn btn-secondary" @click="close">{{ i18n('Close') }}</button></div>
            </div>
        </div>
    </div>
</template>

<script>
import { privilegeResultGroups } from './privilegeResultDetails';

export default {
    name: 'PrivilegeCheckModal',
    data() {
        return {
            pinfl: '', passportNumber: '', preferredCheckId: null, context: null,
            pending: 0, loadingChecks: false, error: null, hasChecked: false, results: [],
            lastTicketLoading: false, lastTicketError: null, lastPrivilegeTicket: null,
            requestSequence: 0, expandedResultId: null,
        };
    },
    computed: {
        loading() {
            return this.loadingChecks || this.pending > 0;
        },
    },
    methods: {
        open(payload = {}) {
            this.pinfl = payload.pinfl || '';
            this.passportNumber = this.normalizePassport(payload.passportNumber || '');
            this.preferredCheckId = payload.preferredCheckId || null;
            this.context = payload.context === undefined ? null : payload.context;
            this.requestSequence++;
            this.pending = 0;
            this.loadingChecks = false;
            this.error = null;
            this.hasChecked = false;
            this.results = [];
            this.lastTicketLoading = false;
            this.lastTicketError = null;
            this.lastPrivilegeTicket = null;
            this.expandedResultId = null;
            $('#privilegeCheckModal').modal('show');
        },
        close() {
            $('#privilegeCheckModal').modal('hide');
        },
        normalizePassport(value) {
            return String(value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 12);
        },
        check() {
            if (this.loading) return;
            if (!/^[3-6]\d{13}$/.test(this.pinfl)) {
                this.error = this.i18n('PINFL must contain 14 digits');
                return;
            }
            if (!this.passportNumber) {
                this.error = this.i18n('Passport details are required');
                return;
            }
            const sequence = ++this.requestSequence;
            const pinfl = this.pinfl;
            const passport = this.passportNumber;
            this.error = null;
            this.hasChecked = false;
            this.results = [];
            this.expandedResultId = null;
            this.loadingChecks = true;
            this.lastTicketLoading = false;
            this.lastTicketError = null;
            this.lastPrivilegeTicket = null;

            this.$api.get('privileges.checks')
                .then(response => {
                    if (sequence !== this.requestSequence) return;
                    const checks = _.get(response, 'data', response);
                    if (!Array.isArray(checks)) throw new Error('Invalid privilege check list');
                    if (!checks.length) {
                        this.error = this.i18n('No privilege checks enabled');
                        return;
                    }
                    this.startChecks(checks, sequence, pinfl, passport);
                })
                .catch(() => {
                    if (sequence === this.requestSequence) this.error = this.i18n('Privilege check could not be completed');
                })
                .finally(() => {
                    if (sequence === this.requestSequence) this.loadingChecks = false;
                });
        },
        startChecks(checks, sequence, pinfl, passport) {
            this.results = checks.map(check => ({ id: check.id, key: check.key, title: check.title, status: 'loading' }));
            if (this.preferredCheckId) {
                this.results.sort((a, b) => Number(b.id === Number(this.preferredCheckId)) - Number(a.id === Number(this.preferredCheckId)));
            }
            this.hasChecked = true;
            this.pending = this.results.length;
            this.lastTicketLoading = true;

            this.$emit('checked', { pinfl, passportNumber: passport, context: this.context });

            this.results.forEach((check, index) => {
                this.$api.post('privileges.check-one', { checkId: check.id, pinfl, passport })
                    .then(response => {
                        if (sequence !== this.requestSequence) return;
                        const result = _.get(response, 'data', response);
                        if (!result || result.id !== check.id || !result.status) throw new Error('Invalid privilege response');
                        this.$set(this.results, index, result);
                    })
                    .catch(error => {
                        if (sequence !== this.requestSequence) return;
                        this.$set(this.results, index, {
                            id: check.id, key: check.key, status: 'error',
                            message: _.get(error, 'response.data.errors.message')
                                || this.i18n('Privilege check could not be completed'),
                        });
                    })
                    .finally(() => {
                        if (sequence === this.requestSequence) this.pending--;
                    });
            });

            this.$api.post('privileges.last-ticket', { pinfl })
                .then(response => {
                    if (sequence === this.requestSequence) this.lastPrivilegeTicket = _.get(response, 'data', response);
                })
                .catch(() => {
                    if (sequence === this.requestSequence) this.lastTicketError = true;
                })
                .finally(() => {
                    if (sequence === this.requestSequence) this.lastTicketLoading = false;
                });
        },
        toggleResult(id) {
            this.expandedResultId = this.expandedResultId === id ? null : id;
        },
        resultGroups(result) {
            return privilegeResultGroups(result);
        },
        isPrivilegeUsed(result) {
            return result.key === 'world_war_ii_participants'
                && (_.get(result, 'data.data.privilegeUse') === true || _.get(result, 'data.privilegeUse') === true);
        },
        badgeClass(status) {
            return {
                found: 'badge-success',
                not_found: 'badge-secondary',
                received: 'badge-info',
                partial: 'badge-warning',
                error: 'badge-danger',
            }[status] || 'badge-secondary';
        },
        statusLabel(status) {
            return {
                found: this.i18n('Found'),
                not_found: this.i18n('Not found'),
                received: this.i18n('Response received'),
                partial: this.i18n('Partial'),
                error: this.i18n('Error'),
            }[status] || status;
        },
        resultTitle(result) {
            const titles = {
                chernobyl_radiation_illness: 'Chernobyl radiation illness',
                nuclear_radiation_veteran: 'Nuclear radiation veteran',
                elderly_disabled_needing_care: 'Elderly disabled needing care',
                accompanying_person: 'Accompanying person',
                students: 'Student',
                disabled_youth_vocational_training: 'Disabled youth vocational training',
                social_registry_youth_competition: 'Social registry youth competition',
                social_protection: 'Social protection registry',
                world_war_ii_participants: 'World War II participants',
            };
            return this.i18n(titles[result.key] || result.title);
        },
        formatMoney(value) {
            return Number(value || 0).toLocaleString() + ' ' + this.i18n('sum');
        },
    },
};
</script>

<style scoped>
.privilege-result-found {
    border-color: #28a745;
}
.privilege-result-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    text-align: left;
    border: 0;
    background: #fff;
    cursor: pointer;
}
.privilege-result-toggle:focus {
    outline: 2px solid #80bdff;
    outline-offset: -2px;
}
.privilege-result-heading-right {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}
.privilege-detail-group + .privilege-detail-group {
    border-top: 1px solid #e9ecef;
    margin-top: 10px;
    padding-top: 10px;
}
</style>
